import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  MapPin, CheckCircle2, FileText, Calendar, Clock, BookOpen, 
  Target, Layers, ChevronRight, Info, ArrowRight, Sparkles, ExternalLink, Trophy, Zap,
  ClipboardList, Users, Building, Globe, Flag
} from "lucide-react";

// Official IRCC Citizenship Journey Milestones
const CITIZENSHIP_MILESTONES = [
  {
    id: 1,
    title: "Application Filed",
    category: "Application",
    description: "Submit your citizenship application online through IRCC portal or by mail. Ensure all documents are complete.",
    tips: ["Complete online application form", "Pay the $630 CAD fee", "Submit all required documents", "Keep copies of everything"],
    icon: FileText,
    typical_wait: "Immediate confirmation"
  },
  {
    id: 2,
    title: "Background Verification",
    category: "Background verification",
    description: "IRCC conducts security and background checks. This includes criminal record checks and identity verification.",
    tips: ["No action required from applicant", "Processing happens automatically", "May request additional documents if needed"],
    icon: Users,
    typical_wait: "1-3 months"
  },
  {
    id: 3,
    title: "Physical Presence Calculation",
    category: "Physical presence",
    description: "IRCC verifies you've been physically present in Canada for at least 1,095 days (3 years) out of the 5 years before applying.",
    tips: ["Keep travel records handy", "Time as a PR counts fully", "Time as temporary resident counts as half"],
    icon: MapPin,
    typical_wait: "Verified during processing"
  },
  {
    id: 4,
    title: "Language Skills Assessment",
    category: "Language skills",
    description: "Prove CLB Level 4 or higher in English or French through approved tests (IELTS, CELPIP, TEF, TCF).",
    tips: ["Book test early", "CLB 4 is equivalent to IELTS 4.0-4.5", "Results valid for 2 years"],
    icon: Globe,
    typical_wait: "Submit with application"
  },
  {
    id: 5,
    title: "Prohibitions Check",
    category: "Prohibitions",
    description: "IRCC checks for any prohibitions that might prevent citizenship, such as removal orders or criminal inadmissibility.",
    tips: ["Disclose any criminal history", "Past issues don't always disqualify", "Be honest in your application"],
    icon: ClipboardList,
    typical_wait: "Part of background check"
  },
  {
    id: 6,
    title: "Citizenship Test Scheduled",
    category: "Citizenship test",
    description: "Once your application is processed, you'll receive an invitation to take the citizenship test (if aged 18-54).",
    tips: ["Study 'Discover Canada' guide", "Test is 30 minutes, 20 questions", "Need 75% (15/20) to pass"],
    icon: Target,
    typical_wait: "Scheduled after file review"
  },
  {
    id: 7,
    title: "Citizenship Test Completed",
    category: "Citizenship test",
    description: "Take the test online or in-person. Results are usually available immediately for online tests.",
    tips: ["Bring valid ID to in-person test", "Online test is proctored", "Can retake if failed"],
    icon: CheckCircle2,
    typical_wait: "Results within days"
  },
  {
    id: 8,
    title: "Oath Ceremony Scheduled",
    category: "Citizenship ceremony",
    description: "After passing all requirements, you'll receive an invitation to attend your citizenship ceremony.",
    tips: ["Ceremony may be virtual or in-person", "Can request postponement if needed", "Dress appropriately"],
    icon: Building,
    typical_wait: "Within weeks of passing test"
  },
  {
    id: 9,
    title: "Citizenship Oath & Certificate",
    category: "Citizenship ceremony",
    description: "Take the Oath of Citizenship and receive your citizenship certificate. Congratulations, you're now a Canadian citizen!",
    tips: ["Bring your PR card", "Photo ID required", "Certificate issued same day"],
    icon: Flag,
    typical_wait: "Same day as ceremony"
  }
];

const JourneyTracker = ({ auth }) => {
  const navigate = useNavigate();
  const [testDate, setTestDate] = useState("");
  const [studyPlan, setStudyPlan] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [expandedMilestone, setExpandedMilestone] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);

  // Load saved study plan on mount
  useEffect(() => {
    const loadSavedPlan = async () => {
      try {
        const response = await axios.get(`${API}/study-plan/saved`, {
          withCredentials: true,
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
        });
        if (response.data.plan) {
          setStudyPlan(response.data.plan);
          if (response.data.plan.test_date) {
            setTestDate(response.data.plan.test_date.split('T')[0]);
          }
        }
      } catch (error) {
        // Silent fail — just means no saved plan
        console.log("No saved plan found");
      } finally {
        setLoadingPlan(false);
      }
    };
    loadSavedPlan();
  }, [auth.token]);

  const generateStudyPlan = async () => {
    if (!testDate) {
      toast.error("Please select your test date");
      return;
    }

    const today = new Date();
    const test = new Date(testDate);
    const daysUntilTest = Math.ceil((test - today) / (1000 * 60 * 60 * 24));

    if (daysUntilTest < 1) {
      toast.error("Please select a future date");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/study-plan/generate`, 
        { test_date: testDate },
        {
          withCredentials: true,
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
        }
      );
      setStudyPlan(response.data);
      toast.success("Study plan generated!");
    } catch (error) {
      console.error("Error generating study plan:", error);
      toast.error("Failed to generate study plan");
    } finally {
      setGenerating(false);
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case "Learn": return "bg-blue-100 text-blue-700 border-blue-200";
      case "Practice": return "bg-amber-100 text-amber-700 border-amber-200";
      case "Test ready": return "bg-green-100 text-green-700 border-green-200";
      case "Crash course": return "bg-red-100 text-red-700 border-red-200";
      default: return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-8" data-testid="journey-tracker">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Citizenship Journey</h1>
          <p className="text-slate-600">Understanding your path to Canadian citizenship</p>
        </div>

        {/* Study Plan Generator */}
        <Card className="border-2 border-maple-100 bg-gradient-to-br from-maple-50/50 to-white" data-testid="study-plan-generator">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-maple" />
              Personalized Study Plan Generator
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-600">
              Enter your citizenship test date and we'll create a detailed day-by-day study schedule tailored just for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 block mb-2">
                  When is your citizenship test?
                </label>
                <Input
                  type="date"
                  value={testDate}
                  onChange={(e) => setTestDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="h-12"
                  data-testid="test-date-input"
                />
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={generateStudyPlan}
                  disabled={generating || !testDate}
                  className="h-12 px-8 bg-maple hover:bg-maple-600 text-white"
                  data-testid="generate-plan-btn"
                >
                  {generating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4 mr-2" />
                      Generate My Plan
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Generated Study Plan */}
            {studyPlan && (
              <div className="mt-6 space-y-6" data-testid="study-plan-result">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900">
                      Your {studyPlan.total_days || studyPlan.days_until_test}-Day Study Plan
                    </h3>
                    <p className="text-sm text-slate-500">
                      Test date: {new Date(studyPlan.test_date).toLocaleDateString('en-CA', { 
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-maple">{studyPlan.total_days}</p>
                    <p className="text-xs text-slate-500">days to prepare</p>
                  </div>
                </div>

                {/* Plan Overview */}
                <div className="grid sm:grid-cols-4 gap-3">
                  {studyPlan.phases?.map((phase, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${getPhaseColor(phase.name)}`}>
                      <p className="font-medium text-sm">{phase.name}</p>
                      <p className="text-xs opacity-75">{phase.days} days</p>
                    </div>
                  ))}
                </div>

                {/* Daily Schedule */}
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {studyPlan.daily_schedule?.map((day, idx) => (
                    <div 
                      key={idx} 
                      className="p-4 bg-white rounded-xl border border-slate-200 hover:border-maple-200 transition-colors"
                      data-testid={`study-day-${day.day}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-maple-100 rounded-full flex items-center justify-center">
                            <span className="font-bold text-maple text-sm">D{day.day}</span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{day.date}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getPhaseColor(day.phase)}`}>
                              {day.phase}
                            </span>
                            {day.is_checkpoint && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 ml-1">
                                Checkpoint
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center text-sm text-slate-500">
                          <Clock className="w-4 h-4 mr-1" />
                          {day.estimated_time || `${day.hours}h`}
                        </div>
                      </div>
                      
                      <div className="space-y-2 ml-13">
                        {day.tasks.map((task, tidx) => {
                          const iconMap = {
                            read: <BookOpen className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />,
                            practice: <Target className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />,
                            flashcard: <Layers className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />,
                            flashcards: <Layers className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />,
                            review: <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />,
                            simulation: <Zap className="w-4 h-4 text-maple mt-0.5 flex-shrink-0" />,
                            mock: <Trophy className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />,
                            checkpoint: <Zap className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />,
                            assessment: <Target className="w-4 h-4 text-maple mt-0.5 flex-shrink-0" />,
                            rest: <Clock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />,
                          };
                          return (
                            <div key={tidx} className="flex items-start gap-2 text-sm group">
                              {iconMap[task.type] || <CheckCircle2 className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
                              <span className="text-slate-700 flex-1">{task.description}</span>
                              {task.link && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(task.link); }}
                                  className="opacity-0 group-hover:opacity-100 text-xs text-maple hover:text-maple-600 flex items-center gap-1 transition-opacity flex-shrink-0"
                                >
                                  Go <ExternalLink className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate("/study")}
                    className="flex-1"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Start Studying
                  </Button>
                  <Button 
                    onClick={() => navigate("/chapter-practice")}
                    className="flex-1 bg-maple hover:bg-maple-600 text-white"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Practice Questions
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Citizenship Journey Timeline */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-maple" />
            <h2 className="font-heading text-xl font-semibold text-slate-900">
              Official Citizenship Journey Stages
            </h2>
          </div>
          <p className="text-slate-600 text-sm">
            Here's what to expect during your Canadian citizenship application process. Click on each stage for more details.
          </p>

          <div className="relative">
            {/* Vertical timeline line */}
            <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-maple via-maple-400 to-green-500" />

            <div className="space-y-4">
              {CITIZENSHIP_MILESTONES.map((milestone, index) => {
                const Icon = milestone.icon;
                const isExpanded = expandedMilestone === milestone.id;
                const isLast = index === CITIZENSHIP_MILESTONES.length - 1;

                return (
                  <div key={milestone.id} className="relative flex gap-4" data-testid={`milestone-${milestone.id}`}>
                    {/* Timeline dot */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
                        isLast 
                          ? 'bg-green-500 ring-4 ring-green-100' 
                          : 'bg-white border-2 border-maple ring-4 ring-maple-50'
                      }`}>
                        <Icon className={`w-5 h-5 ${isLast ? 'text-white' : 'text-maple'}`} />
                      </div>
                    </div>

                    {/* Content */}
                    <Card 
                      className={`flex-1 cursor-pointer transition-all duration-200 border ${
                        isExpanded ? 'border-maple shadow-md' : 'border-slate-200 hover:border-maple-200'
                      }`}
                      onClick={() => setExpandedMilestone(isExpanded ? null : milestone.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-maple bg-maple-50 px-2 py-0.5 rounded-full">
                                {milestone.category}
                              </span>
                              <span className="text-xs text-slate-400">•</span>
                              <span className="text-xs text-slate-500">{milestone.typical_wait}</span>
                            </div>
                            <h3 className="font-heading font-semibold text-slate-900">{milestone.title}</h3>
                            <p className="text-sm text-slate-600 mt-1">{milestone.description}</p>
                          </div>
                          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`} />
                        </div>

                        {/* Expanded tips */}
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-slate-100">
                            <div className="flex items-center gap-2 mb-2">
                              <Info className="w-4 h-4 text-truenorth" />
                              <span className="text-sm font-medium text-slate-700">Tips & Information</span>
                            </div>
                            <ul className="space-y-2">
                              {milestone.tips.map((tip, tipIdx) => (
                                <li key={tipIdx} className="flex items-start gap-2 text-sm text-slate-600">
                                  <ArrowRight className="w-3 h-3 text-maple mt-1 flex-shrink-0" />
                                  {tip}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Info Card */}
        <Card className="border-0 shadow-sm bg-truenorth-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-truenorth rounded-lg flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-slate-900 mb-2">Processing Times</h3>
                <p className="text-slate-700 text-sm">
                  Current processing time for citizenship applications is approximately 12-24 months from submission to ceremony. 
                  Times vary based on application volume and individual circumstances. Check the 
                  <a href="https://www.canada.ca/en/immigration-refugees-citizenship/services/application/check-processing-times.html" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="text-truenorth hover:underline ml-1">
                    official IRCC website
                  </a> for current estimates.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default JourneyTracker;

